import type { AugmentedAccount, AugmentedUser } from '@/services/auth/types';
import * as Sentry from '@sentry/react';

import { createBrowserRouter } from 'react-router-dom';

function shouldSentryWork() {
  return ['standalone', 'prod-staging-1', 'prod-staging-2', 'production'].includes(__CIO_ENV__);
}

export function setupSentry() {
  if (!shouldSentryWork()) {
    return;
  }

  // Only enable browser tracing conditionally while we sort out some
  // CORS issues on the backend. Should be removed once it's fixed.
  let isBrowserTracingEnabled = false;

  try {
    isBrowserTracingEnabled = localStorage.getItem('hydra:sentryBrowserTracing') === 'true';
  } catch {
    // localStorage might fail under some circumstances, just ignore it
  }

  Sentry.init({
    dsn: 'https://de97274fa5d848cca91d899b8df33337@o38230.ingest.sentry.io/4505875619905536',
    integrations: [
      Sentry.extraErrorDataIntegration(),
      ...(isBrowserTracingEnabled ? [Sentry.browserTracingIntegration()] : []),
    ],

    tracesSampleRate: isBrowserTracingEnabled ? 1 : 0,
    // Prevents CORS issues. Can be removed once/if we adjust the backend
    // https://docs.sentry.io/platforms/javascript/tracing/instrumentation/automatic-instrumentation/#tracePropagationTargets
    tracePropagationTargets: [],

    environment: __CIO_ENV__,
    release: `hydra-ui@${__HYDRA__APP__VERSION__}+${__COMMIT_HASH__}+${__CIO_ENV__}`,
  });
}

// Sentry does come with a native FullStory integration: https://github.com/getsentry/sentry-fullstory
// but that requires using FullStory's SDK: https://github.com/fullstorydev/fullstory-browser-sdk
// We don't currently do that - we inject FullStory via our "tracking-scripts"
// utilities, so we need to set up FullStory manually.
// If/when we move to the FullStory SDK, we can also use the Sentry/FullStory
// integration.
export function setSentryFullStorySessionUrl() {
  window._fs_ready = function () {
    if (window?.FS?.getCurrentSessionURL != null) {
      const scope = Sentry.getCurrentScope();
      // The first argument to getCurrentSessionURL (true) ensures that we're getting the exact moment in time.
      scope.setExtra('fs_session_url', window.FS.getCurrentSessionURL(true));
    }
  };
}

export function setSentryMasqueradingContext(isMasqOn: boolean) {
  if (!shouldSentryWork()) {
    return;
  }

  Sentry.setExtra('masquerading_enabled', isMasqOn);
}

export function sentrySetDataCenter(dataCenter: string) {
  Sentry.getCurrentScope().setTag('data_center', dataCenter);
}

export function sentrySetUser(account: AugmentedAccount, user: AugmentedUser) {
  Sentry.setUser({ id: user.id, email: user.email, account_id: account.id });
}

export function sentryCaptureLegacyRedirect(path: string) {
  // Disabling temporarily, since 1: we are not planning to handle these legacy redirects in the near future,
  // 2: we have collected a lot of data on these redirects already and 3: this is spamming our logs

  // Not removing this altogether yet though because we might want to re-enable it in the future.
  // For more info on why this was added, https://github.com/customerio/hydra/pull/2054
  return;

  Sentry.withScope((scope) => {
    scope.setExtra('referrer', document.referrer);
    Sentry.captureMessage(`Legacy redirect used: ${path}`, scope);
  });
}

export const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
export const sentryCaptureException = Sentry.captureException;
